import IconHistory from "assets/img/icon-history.svg?react"

import { FC, useMemo, useState } from "react"
import { Dayjs } from "utils/dayjs"

import Button from "components/Button/Button"
import { useTranslation } from "react-i18next"
import { printPeriod } from "utils/utils"
import useAssetVersions from "./hooks/useAssetVersions"
import { Guid, IApiLink } from "@/types"
import toastMessages from "stores/toastMessages"
import Spinner from "components/Spinner/Spinner"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"

interface IAssetVersionsControlProps {
    isCurrentVersion: boolean
    version?: number
    startDate?: Dayjs | null
    endDate?: Dayjs | null
    linkGetAssetVersions?: IApiLink
    onSwitchToVersion: (assetId: Guid) => void
}

const AssetVersionsControl: FC<IAssetVersionsControlProps> = ({
    isCurrentVersion,
    version,
    startDate,
    endDate,
    linkGetAssetVersions,
    onSwitchToVersion,
}) => {
    const { t } = useTranslation()
    const [isDropdownInit, setIsDropdownInit] = useState(false)
    const { assetVersions } = useAssetVersions(isDropdownInit ? linkGetAssetVersions : undefined, toastMessages.showSystemError)

    const buttonLabel = isCurrentVersion
        ? t('asset.currentVersion')
        : `${t('asset.version')}${typeof version === "number" ? ` ${version}` : ''}: ${printPeriod({ start: startDate, end: endDate })}`

    const assetVersionsList = useMemo(() => {
        if (!assetVersions) {
            return (
                <div className="px-8 py-4">
                    <Spinner />
                </div>
            )
        }

        if (!assetVersions.length) {
            return (
                <div className="p-4 text-center color-grey-500">
                    {t('common.noResultsFound')}
                </div>
            )
        }

        return (
            <InfoDropdownList>
                {assetVersions.map(assetVersion => (
                    <InfoDropdownListItemButton
                        key={assetVersion.version}
                        onClick={() => onSwitchToVersion(assetVersion.assetId)}>
                        {assetVersion.isCurrentVersion
                            ? t('asset.currentVersion')
                            : `${t('asset.version')} ${assetVersion.version}: ${printPeriod({ start: assetVersion.availabilityDate, end: assetVersion.disposalDate })}`
                        }
                    </InfoDropdownListItemButton>
                ))}

            </InfoDropdownList>
        )
    }, [assetVersions, onSwitchToVersion, t])

    if (!linkGetAssetVersions) return null

    return (
        <InfoDropdown
            label={() => (
                <Button
                    theme="link"
                    round
                    icon={<IconHistory width="18" height="18" />}
                    label={buttonLabel}
                />
            )}
            placement="bottom-start"
            onOpen={() => setIsDropdownInit(true)}
            content={assetVersionsList} />
    )
}

export default AssetVersionsControl
